/**
 * Client wrapper for the athlete-level fueling profile (`/api/fueling-profile`)
 * — sodium, gut level, the single gut-training ramp, per-sport carb targets.
 * ONE row per athlete: an edit here affects every race plan and standalone
 * generate, so a successful save broadcasts BOTH `FUELING_PROFILE_CHANGED` and
 * the journey-changed signal (rails/settings/race-day tiles re-read their
 * profile-derived view fields; the server has already kicked off drop/preview
 * regeneration, plan D8).
 */
import { apiCall, apiCallWithCsrfRefresh } from "./api";
import { notifyJourneyChanged } from "./journeyApi";

const BASE = "/api/fueling-profile";

export type SweatSodiumSource = "explicit" | "proxy";
export type GutLevel = "new" | "tuning" | "locked";
export type RampUiState = "pending" | "active" | "full" | "completed";

export type FuelingProfile = {
  gutLevel: GutLevel | null;
  sodiumTarget: number | null;
  sodiumSource: SweatSodiumSource | null;
  perSportTargets: Record<string, number> | null;
  rampEnabled: boolean;
  rampConfig: { startPct: number; weeks: number; startedYmd: string; jumpWeeks?: number } | null;
};

/** Server-computed ramp position — never re-derive "week N of M" (F2/D9). */
export type FuelingProfileRamp = {
  state: RampUiState;
  weekNow: number;
  weeksTotal: number;
  fullYmd: string;
  /** Set once the ramp finishes and the server auto-disables it (A2). */
  completedYmd: string | null;
  /** Server-surfaced advisory (e.g. race compression too tight) — inline note (B9). */
  warning: string | null;
  /** Set when the soonest active race compresses the ramp (R6 explainer). */
  compressedForRace: { raceYmd: string; raceName: string | null } | null;
};

export type FuelingProfileView = {
  /** Null until the athlete first saves anything (R15) — treat as defaults. */
  profile: FuelingProfile | null;
  ramp: FuelingProfileRamp | null;
};

export type FuelingProfilePatch = {
  gutLevel?: GutLevel | null;
  sodiumTarget?: number | null;
  sodiumSource?: SweatSodiumSource | null;
  perSportTargets?: Record<string, number> | null;
  rampEnabled?: boolean;
  rampConfig?: {
    startPct: number;
    weeks: number;
    startedYmd: string;
    jumpWeeks?: number;
  } | null;
};

export const FUELING_PROFILE_CHANGED_EVENT = "fg:fueling-profile-changed";

/** Dispatch the cross-component "fueling profile changed" signal (R2). */
export function notifyFuelingProfileChanged(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(FUELING_PROFILE_CHANGED_EVENT));
  }
}

export async function getFuelingProfile(signal?: AbortSignal): Promise<FuelingProfileView> {
  return apiCall<FuelingProfileView>(BASE, { signal });
}

/**
 * Upsert the athlete's profile. Broadcasts the change signals on success so
 * every journey surface refetches its profile-derived fields.
 */
export async function patchFuelingProfile(
  patch: FuelingProfilePatch,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<FuelingProfileView> {
  const view = await apiCallWithCsrfRefresh<FuelingProfileView>(
    BASE,
    { method: "PATCH", body: patch, csrfToken },
    refreshCsrf
  );
  notifyFuelingProfileChanged();
  notifyJourneyChanged();
  return view;
}
